import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Chip,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Unstable_Grid2 as Grid
} from '@mui/material';
import PillTabs from 'components/shared/tabs/PillTabs';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import OrgExpandableRow from 'components/org-client/OrgExpandableRow';
import PrivacyNote from 'components/org-client/PrivacyNote';
import ProtectedBadge from 'components/org-client/ProtectedBadge';
import WellbeingIndexGauge from 'components/org-client/WellbeingIndexGauge';
import useOrgPageTitle from 'components/org-client/useOrgPageTitle';
import useLogger from 'hooks/useLogger';
import { bgOf, toneOf } from 'components/admin-organizations/wellbeingTones';
import {
  MY_ORGANIZATION_DEPARTMENTS,
  MY_ORGANIZATION_HEATMAP,
  MY_ORGANIZATION_WELLBEING
} from 'api/graphql/orgClient';

const DIMENSIONS = ['energy', 'stress', 'rest', 'motivation'];
const DIMENSION_DEFAULTS = {
  energy: 'Energía',
  stress: 'Estrés',
  rest: 'Descanso',
  motivation: 'Motivación'
};
const WINDOWS = [4, 8, 12];

/**
 * Tablero de bienestar de la empresa: índice, dimensiones, tendencia y mapa por departamento.
 * Es de SOLO LECTURA por diseño. Todo lo que llega ya viene agregado y con k-anonymity del
 * backend; aquí no hay forma de bajar a una persona.
 */
function OrgClientWellbeing() {
  const { t } = useTranslation(['orgClient']);
  const { logError } = useLogger();
  const pageTitle = t('menu.wellbeing', { defaultValue: 'Bienestar' });
  useOrgPageTitle(pageTitle);

  const [windowIndex, setWindowIndex] = useState(0);
  const [departmentId, setDepartmentId] = useState('');
  const weeks = WINDOWS[windowIndex];

  const { data: summaryData, loading, error } = useQuery(MY_ORGANIZATION_WELLBEING, {
    variables: { weeks, department_id: departmentId || null },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    onError: (queryError) => logError('OrgClientWellbeing', queryError)
  });
  const { data: heatmapData } = useQuery(MY_ORGANIZATION_HEATMAP, {
    variables: { weeks },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    onError: (queryError) => logError('OrgClientWellbeing', queryError)
  });
  const { data: departmentsData } = useQuery(MY_ORGANIZATION_DEPARTMENTS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    onError: (queryError) => logError('OrgClientWellbeing', queryError)
  });

  const summary = summaryData?.myOrganizationWellbeing || null;
  const heatmap = useMemo(() => heatmapData?.myOrganizationHeatmap || [], [heatmapData]);
  const departments = departmentsData?.myOrganizationDepartments || [];

  const dimensionLabel = (dimension) =>
    t(`dimensions.${dimension}`, { defaultValue: DIMENSION_DEFAULTS[dimension] || dimension });

  // Empresa chica: si ni el índice ni una sola celda del mapa superan el umbral, la pantalla
  // no tiene nada que mostrar y hay que decir POR QUÉ, no dejarla en blanco.
  const everythingProtected =
    Boolean(summary?.protected) && heatmap.every((row) => row.protected);

  const trend = summary?.trend || [];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <SEOPageTitle title={pageTitle} />

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ md: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2.5 }}>
        <PillTabs
          value={windowIndex}
          onChange={(event, next) => setWindowIndex(next)}
          ariaLabel={t('filters.windowAria', { defaultValue: 'Ventana de tiempo' })}
          tabs={WINDOWS.map((option) => ({
            key: `w-${option}`,
            label: t('filters.weeks', { count: option, defaultValue: '{{count}} semanas' })
          }))}
        />
        <TextField
          select
          size="small"
          label={t('filters.department', { defaultValue: 'Departamento' })}
          value={departmentId}
          onChange={(event) => setDepartmentId(event.target.value)}
          sx={{ minWidth: { xs: '100%', md: 220 } }}>
          <MenuItem value="">{t('filters.allDepartments', { defaultValue: 'Toda la empresa' })}</MenuItem>
          {departments.map((department) => (
            <MenuItem key={department.id} value={department.id}>
              {department.name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {error && !summary && (
        <Alert severity="error" variant="filled" sx={{ borderRadius: 3, mb: 2.5 }}>
          {t('errors.wellbeing', {
            defaultValue: 'No pudimos cargar el bienestar de tu empresa. Intenta de nuevo en un momento.'
          })}
        </Alert>
      )}

      {loading && !summary ? (
        <Grid container spacing={2.5}>
          <Grid xs={12} md={5}>
            <Skeleton variant="rounded" height={360} sx={{ borderRadius: 4 }} />
          </Grid>
          <Grid xs={12} md={7}>
            <Skeleton variant="rounded" height={360} sx={{ borderRadius: 4 }} />
          </Grid>
        </Grid>
      ) : (
        summary && (
          <Grid container spacing={2.5}>
            <Grid xs={12} md={5}>
              <PremiumCard sx={{ height: '100%' }} innerSx={{ height: '100%' }}>
                <Stack spacing={2} alignItems="center" sx={{ py: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      textAlign: 'center'
                    }}>
                    {t('wellbeing.indexTitle', { defaultValue: 'Índice OTRIX de bienestar' })}
                  </Typography>
                  <WellbeingIndexGauge score={summary.score} isProtected={summary.protected} />
                  {summary.protected ? (
                    <Stack spacing={0.75} alignItems="center">
                      <ProtectedBadge />
                      <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 280 }}>
                        {t('privacy.protectedReason', {
                          defaultValue:
                            'Se necesitan al menos 5 respuestas para mostrar un dato. Con menos gente, alguien podría ser identificado.'
                        })}
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                      {t('wellbeing.respondents', {
                        respondents: summary.respondents,
                        members: summary.members,
                        percent: Math.round(summary.participation_percent),
                        defaultValue: '{{respondents}} de {{members}} personas respondieron ({{percent}}%)'
                      })}
                    </Typography>
                  )}

                  <Stack spacing={1.25} sx={{ width: '100%', pt: 1 }}>
                    {DIMENSIONS.map((dimension) => {
                      const row = (summary.dimensions || []).find((item) => item.dimension === dimension);
                      const isProtected = Boolean(row?.protected);
                      const score = isProtected ? null : row?.score ?? null;

                      return (
                        <Stack key={dimension} direction="row" spacing={1} alignItems="center">
                          <Typography variant="caption" sx={{ width: 86, flex: 'none', fontWeight: 700 }}>
                            {dimensionLabel(dimension)}
                          </Typography>
                          <Box
                            sx={{
                              flex: 1,
                              height: 6,
                              borderRadius: 999,
                              backgroundColor: 'surface.overlay',
                              overflow: 'hidden'
                            }}>
                            <Box
                              sx={{
                                height: '100%',
                                width: '100%',
                                borderRadius: 999,
                                backgroundColor: toneOf(score),
                                transform: `scaleX(${(score || 0) / 100})`,
                                transformOrigin: 'left',
                                transition: 'transform 600ms cubic-bezier(0.32, 0.72, 0, 1)'
                              }}
                            />
                          </Box>
                          <Box sx={{ width: 38, display: 'flex', justifyContent: 'flex-end' }}>
                            {isProtected ? (
                              <ProtectedBadge compact />
                            ) : (
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: toneOf(score) }}>
                                {score ?? '--'}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      );
                    })}
                  </Stack>
                </Stack>
              </PremiumCard>
            </Grid>

            <Grid xs={12} md={7}>
              <PremiumCard sx={{ height: '100%' }} innerSx={{ height: '100%' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                  {t('wellbeing.trendTitle', { defaultValue: 'Tendencia del índice' })}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('wellbeing.trendHint', { defaultValue: 'Una barra por semana. Las semanas protegidas quedan vacías.' })}
                </Typography>
                {trend.length === 0 ? (
                  <Box sx={{ pt: 2 }}>
                    <PrivacyNote variant="empty" />
                  </Box>
                ) : (
                  <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ height: 180, mt: 2 }}>
                    {trend.map((point) => (
                      <Tooltip
                        key={point.week_key}
                        title={
                          point.protected
                            ? t('privacy.protected', { defaultValue: 'Protegido por privacidad' })
                            : `${point.week_key}: ${point.score ?? '--'}`
                        }>
                        <Stack
                          spacing={0.5}
                          alignItems="center"
                          sx={{ flex: 1, minWidth: 0, height: '100%', justifyContent: 'flex-end' }}>
                          <Box
                            sx={{
                              width: '100%',
                              maxWidth: 34,
                              height: `${point.score ? Math.max(8, point.score) : 4}%`,
                              borderRadius: '6px 6px 2px 2px',
                              backgroundColor: point.score ? toneOf(point.score) : 'surface.overlay',
                              border: '1px solid',
                              borderColor: point.score ? 'transparent' : 'hairline.main',
                              transition: 'height 500ms cubic-bezier(0.32, 0.72, 0, 1)'
                            }}
                          />
                          <Typography variant="caption" noWrap sx={{ color: 'text.disabled', fontSize: '0.58rem' }}>
                            {point.week_key.slice(-3)}
                          </Typography>
                        </Stack>
                      </Tooltip>
                    ))}
                  </Stack>
                )}
              </PremiumCard>
            </Grid>

            <Grid xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                {t('wellbeing.heatmapTitle', { defaultValue: 'Mapa por departamento' })}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
                {t('wellbeing.heatmapHint', { defaultValue: 'Abre un departamento para ver sus dimensiones.' })}
              </Typography>

              {heatmap.length === 0 ? (
                <PrivacyNote variant="empty" />
              ) : (
                <Stack spacing={1}>
                  {heatmap.map((row) => (
                    <OrgExpandableRow
                      key={row.department_id || 'none'}
                      title={row.department_name || t('wellbeing.noDepartment', { defaultValue: 'Sin departamento' })}
                      subtitle={t('wellbeing.departmentMeta', {
                        members: row.members,
                        percent: Math.round(row.participation_percent || 0),
                        defaultValue: '{{members}} personas · {{percent}}% de participación'
                      })}
                      trailing={
                        row.protected ? (
                          <ProtectedBadge compact />
                        ) : (
                          <Chip
                            size="small"
                            label={row.overall ?? '--'}
                            sx={{
                              flex: 'none',
                              fontWeight: 800,
                              fontVariantNumeric: 'tabular-nums',
                              color: toneOf(row.overall),
                              backgroundColor: bgOf(row.overall)
                            }}
                          />
                        )
                      }>
                      {row.protected ? (
                        <Stack spacing={1} sx={{ pt: 1 }}>
                          <ProtectedBadge />
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {t('privacy.departmentProtected', {
                              defaultValue:
                                'Este departamento tiene menos de 5 respuestas en la ventana elegida, así que su bienestar no se publica. Amplía la ventana o espera a que más personas respondan.'
                            })}
                          </Typography>
                        </Stack>
                      ) : (
                        <Grid container spacing={1.5} sx={{ pt: 1 }}>
                          {DIMENSIONS.map((dimension) => {
                            const cell = (row.cells || []).find((item) => item.dimension === dimension);
                            const isProtected = Boolean(cell?.protected);

                            return (
                              <Grid key={dimension} xs={6} sm={3}>
                                <Box
                                  sx={{
                                    p: 1.5,
                                    borderRadius: '10px',
                                    textAlign: 'center',
                                    border: '1px solid',
                                    borderColor: 'hairline.main',
                                    backgroundColor: isProtected ? 'surface.overlay' : bgOf(cell?.score)
                                  }}>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: 'text.secondary', display: 'block', fontWeight: 700 }}>
                                    {dimensionLabel(dimension)}
                                  </Typography>
                                  {isProtected ? (
                                    <Box sx={{ pt: 0.5 }}>
                                      <ProtectedBadge compact />
                                    </Box>
                                  ) : (
                                    <Typography
                                      sx={{
                                        fontWeight: 800,
                                        fontVariantNumeric: 'tabular-nums',
                                        color: toneOf(cell?.score)
                                      }}>
                                      {cell?.score ?? '--'}
                                    </Typography>
                                  )}
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      )}
                    </OrgExpandableRow>
                  ))}
                </Stack>
              )}
            </Grid>

            <Grid xs={12}>
              <PrivacyNote variant={everythingProtected ? 'empty' : 'default'} />
            </Grid>
          </Grid>
        )
      )}
    </Box>
  );
}

export default OrgClientWellbeing;
